import React, { useState, useEffect, ChangeEvent } from "react";
import Typography from "style-guide/component/Typography";

interface CompanyLogoProps {
  logoUrl: string;
  onUpload: (file: File) => Promise<void>;
  companyName: string;
}

const Company: React.FC<CompanyLogoProps> = ({ logoUrl, onUpload, companyName }) => {
  const [previewUrl, setPreviewUrl] = useState<string>(logoUrl);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPreviewUrl(logoUrl);
  }, [logoUrl]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isLt2MB = file.size / 1024 / 1024 < 2;

    if (!isImage || !isLt2MB) {
      setError("Only images smaller than 2MB are allowed.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await onUpload(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    } catch (err) {
      setError("Failed to upload logo. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex gap-4 py-6 border-t-2 border-[whitesmoke]">
        <Typography variant="Heading 3" className="w-64" text="Company Details" />
        <div className="flex flex-col gap-6">
          {companyName && <div className="flex gap-2">
            <Typography variant="Heading 5" text="Company Name :" />
            <Typography variant="Heading 5" text={companyName} />
          </div>}
          <div className="flex gap-4 items-center">
          <div
            className="w-32 h-32 border border-dashed bg-white flex items-center justify-center cursor-pointer hover:opacity-80"
            onClick={() => previewUrl && setModalOpen(true)}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Company Logo"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            ) : (
              <Typography variant="Base" text="No logo uploaded" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="logo-upload"
              className="border border-gray-300 px-4 py-2 rounded-md text-sm bg-white cursor-pointer hover:bg-gray-100"
            >
              {loading ? "Uploading..." : "Change Logo"}
            </label>
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              disabled={loading}
            />
            {error && (
              <Typography
                variant="Base"
                className="text-red-500 text-sm"
                text={error}
              />
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Modal for Image Preview */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="relative max-w-3xl w-full p-4 bg-white rounded-lg shadow-lg">
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-black text-xl"
              onClick={() => setModalOpen(false)}
            >
              &times;
            </button>
            <img
              src={previewUrl}
              alt="Full Preview"
              className="w-full h-auto object-contain max-h-[80vh] p-4"
              loading="lazy"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Company;
